/** @jsx jsx */

/** @jsxFrag React.Fragment */
import { css, jsx } from '@emotion/react';

import * as React from 'react';
import { useEffect } from 'react';

import config from 'js/app/config';
import Retracked from 'js/app/retracked';
import path from 'js/lib/path';

import { Grid, Hidden, breakpoints } from '@coursera/cds-core';

import GrowthDiscoveryExperiments from 'bundles/epic/clients/GrowthDiscovery';
import PaginationControls from 'bundles/page/components/cds/PaginationControls';
import { ALL_PRODUCTS_INDEX_TYPE, INDEX_TYPE_TO_NAME_EXPERIMENT_PARAMETER_NAME } from 'bundles/search-common/constants';
import CourseraMetatags from 'bundles/seo/components/CourseraMetatags';
import ArticleBreadcrumbs from 'bundles/shared-articles/components/ArticleBreadcrumbs';
import HeroBanner from 'bundles/shared-articles/components/ArticleHubHeroBanner';
import CategoryPageGoogleSchemaMarkup from 'bundles/shared-articles/components/seo/CategoryPageGoogleSchemaMarkup';
import useArticleUrlData from 'bundles/shared-articles/hooks/useArticleUrlData';
import { getPageNumberFromString, isValidPageNumber } from 'bundles/shared-articles/utils';
import { generatePaginationProperties } from 'bundles/shared-articles/utils/seoUtils';
import ArticleHubCardWithEstimatedReadTime from 'bundles/unified-seo-content-common/components/hub-page/ArticleHubCardWithEstimatedReadTime';
import CategoryNavigation from 'bundles/unified-seo-content-common/components/hub-page/CategoryNavigation';
import Layout from 'bundles/unified-seo-content-common/components/hub-page/Layout';
import ProductRecommendationsDelegator from 'bundles/unified-seo-content-common/components/hub-page/ProductRecommendationsDelegator';
import RichTextContentRenderer from 'bundles/unified-seo-content-common/components/rich-text/RichTextContentRenderer';
import PageDataErrorHandler from 'bundles/unified-seo-content-common/components/shared/PageDataErrorHandler';
import HubPageSkeleton from 'bundles/unified-seo-content-common/components/skeletons/HubPageSkeleton';
import {
  ITEMS_PER_CATEGORY_PAGE,
  NUMBER_OF_ITEMS_PER_LINE_DESKTOP,
  NUMBER_OF_ITEMS_PER_LINE_MOBILE,
  NUMBER_OF_RECOMMENDATIONS_TO_SHOW_ON_DESKTOP,
  SEO_CONTENT_PAGE_TYPE,
} from 'bundles/unified-seo-content-common/constants';
import useArticleFilterQuery from 'bundles/unified-seo-content-common/hooks/useArticleFilterQuery';
import useDiscoveryCollectionsRecs from 'bundles/unified-seo-content-common/hooks/useDiscoveryCollectionsRecs';
import useSeoContentCategoryPageQuery from 'bundles/unified-seo-content-common/hooks/useSeoContentCategoryPageQuery';
import useSeoContentHubPageLink from 'bundles/unified-seo-content-common/hooks/useSeoContentHubPageLink';
import type { SeoContentCard } from 'bundles/unified-seo-content-common/types';
import { scrollPageToTop } from 'bundles/unified-seo-content-common/utils';

import _t from 'i18n!nls/articles';

type ArticlesRowProps = {
  articles: Array<SeoContentCard | null>;
  isFeaturedContentRow?: boolean;
};

const styles = {
  rowsContainer: css`
    display: flex;
    flex-direction: column;
  `,
  fullWidthContainerContent: css`
    margin: 0 auto;
  `,
  fullWidthContainer: css`
    width: calc(100% + var(--cds-spacing-600));
    border-radius: var(--cds-spacing-100);
    background-color: var(--cds-color-neutral-background-primary-weak);
    padding: var(--cds-spacing-300);
    margin-left: calc(var(--cds-spacing-300) * -1);
    margin-bottom: var(--row-spacing-desktop-in-px);
    ${breakpoints.down('md')} {
      margin-bottom: var(--row-spacing-mobile-in-px);
    }
  `,
  articleCard: css`
    padding: 0 var(--cds-spacing-200) var(--row-spacing-desktop-in-px);
    ${breakpoints.down('md')} {
      padding: 0 var(--cds-spacing-100) var(--row-spacing-mobile-in-px);
    }
  `,
  pagination: css`
    margin-bottom: var(--row-spacing-desktop-in-px);
    ${breakpoints.down('md')} {
      margin-bottom: var(--row-spacing-mobile-in-px);
    }
  `,
};

const ArticlesRow = ({ articles, isFeaturedContentRow }: ArticlesRowProps) => {
  if (!articles?.length) {
    return null;
  }
  return (
    <Grid container>
      {articles.map(
        (article) =>
          article?.slug && (
            <Grid item xs={6} md={4} key={article.slug} css={styles.articleCard}>
              <ArticleHubCardWithEstimatedReadTime
                title={article.title || _t('Coursera Article')}
                subtitle={article.intro || _t('This is a Coursera Article')}
                slug={article.slug}
                metadata={{
                  publishedDate: article.sys.firstPublishedAt,
                }}
                image={{
                  url: article.featuredImage?.url || '',
                  description: article.featuredImage?.description || _t('Article Image'),
                }}
                localizedCountryCode={article?.countryCode ?? undefined}
                isFeatured={isFeaturedContentRow}
              />
            </Grid>
          )
      )}
    </Grid>
  );
};

export const CategoryPage = () => {
  const urlDomain = config.url.domain;
  const { page, slug, pathname, isPreview, subfolderLocaleCode } = useArticleUrlData();
  const currentPageNumber = getPageNumberFromString(page);

  useEffect(() => {
    scrollPageToTop();
  }, [currentPageNumber]);

  const articlesSkip = (currentPageNumber - 1) * ITEMS_PER_CATEGORY_PAGE;
  const indexTypeToNameMap = () => GrowthDiscoveryExperiments.get(INDEX_TYPE_TO_NAME_EXPERIMENT_PARAMETER_NAME);
  const productRecsIndexName = indexTypeToNameMap()[ALL_PRODUCTS_INDEX_TYPE];

  const {
    title,
    introduction,
    featuredImageUrl,
    featuredImageAlt,
    featuredContent,
    bottomContentLinks,
    bottomContentJson,
    discoveryCollectionSetId,
    isHubPageDataLoading,
    productRecommendationTitle,
    productRecommendationsSearchUrl,
    navigationLinks,
    contentFilters,
  } = useSeoContentCategoryPageQuery({
    slug,
    pageType: SEO_CONTENT_PAGE_TYPE.articles,
    preview: isPreview,
    subfolderLocaleCode,
  });

  const { articles, totalNumberOfArticles, isSeoContentDataLoading } = useArticleFilterQuery({
    filters: contentFilters,
    articlesSkip,
    preview: isPreview,
  });

  const hubPageLink = useSeoContentHubPageLink({
    pageType: SEO_CONTENT_PAGE_TYPE.articles,
    preview: isPreview,
    subfolderLocaleCode,
  });
  const { recommendationCollections, loadingRecommendations } = useDiscoveryCollectionsRecs(
    discoveryCollectionSetId || ''
  );
  const pageCount = totalNumberOfArticles ? Math.ceil(totalNumberOfArticles / ITEMS_PER_CATEGORY_PAGE) : 0;

  const missingRequiredFields = !articles || !title || !introduction || !featuredImageUrl || !featuredContent;

  const isCategoryPageDataLoading = isHubPageDataLoading || isSeoContentDataLoading;

  if (isCategoryPageDataLoading) {
    return <HubPageSkeleton />;
  }

  if (missingRequiredFields || !isValidPageNumber(currentPageNumber, pageCount)) {
    return <PageDataErrorHandler />;
  }

  const pageUrl = path.join(urlDomain, pathname);

  const metaTitle = _t('#{title} Career Education, Tips & Insights', {
    title,
  });
  const metaDescription = _t('Find #{title} career education resources, career development, tips, insights and more.', {
    title,
  });

  const paginationProperties = generatePaginationProperties({ pageCount, currentPageNumber, pageUrl });
  const hasDiscoveryCollectionRecs = !!recommendationCollections.length;
  const shouldShowProductRecsSection = hasDiscoveryCollectionRecs || !!productRecommendationsSearchUrl;
  const hasMultipleDiscoveryCollectionRecs = hasDiscoveryCollectionRecs && recommendationCollections.length > 1;

  const shouldShowFeaturedContent = currentPageNumber === 1;

  return (
    <React.Fragment>
      <Layout
        heroBannerComponent={
          <HeroBanner
            title={title}
            subtitle={introduction}
            image={{ src: featuredImageUrl, alt: featuredImageAlt || _t('Coursera Articles Hero Banner') }}
            breadCrumbComponent={
              <ArticleBreadcrumbs title={title} links={[{ label: _t('Articles'), href: hubPageLink }]} />
            }
          />
        }
        navigationComponent={<CategoryNavigation activeCategorySlug={slug} navigationLinks={navigationLinks} />}
        contentComponent={
          <div
            css={[
              styles.rowsContainer,
              css`
                --row-spacing-desktop-in-px: var(--cds-spacing-800);
                --row-spacing-mobile-in-px: var(--cds-spacing-400);
              `,
            ]}
          >
            {shouldShowFeaturedContent && <ArticlesRow articles={featuredContent} isFeaturedContentRow />}
            <Hidden smDown>
              <ArticlesRow articles={articles.slice(0, NUMBER_OF_ITEMS_PER_LINE_DESKTOP)} />
            </Hidden>
            <Hidden mdUp>
              <ArticlesRow articles={articles.slice(0, NUMBER_OF_ITEMS_PER_LINE_MOBILE)} />
            </Hidden>
            {shouldShowProductRecsSection && (
              <div css={styles.fullWidthContainer}>
                <div css={styles.fullWidthContainerContent}>
                  <ProductRecommendationsDelegator
                    shouldUseDiscoveryCollections={!!discoveryCollectionSetId}
                    recommendationCollections={hasDiscoveryCollectionRecs ? [recommendationCollections[0]] : []}
                    loadingDiscoveryCollectionsRecommendations={loadingRecommendations}
                    productRecsByAlgoliaMetadata={{
                      title,
                      productRecommendationTitle,
                      productRecommendationsSearchUrl,
                      productRecsIndexName,
                      hitsPerPage: NUMBER_OF_RECOMMENDATIONS_TO_SHOW_ON_DESKTOP,
                    }}
                  />
                </div>
              </div>
            )}
            <Hidden smDown>
              <ArticlesRow articles={articles.slice(NUMBER_OF_ITEMS_PER_LINE_DESKTOP)} />
            </Hidden>
            <Hidden mdUp>
              <ArticlesRow articles={articles.slice(NUMBER_OF_ITEMS_PER_LINE_MOBILE)} />
            </Hidden>
            {hasMultipleDiscoveryCollectionRecs && (
              <div css={styles.fullWidthContainer}>
                <div css={styles.fullWidthContainerContent}>
                  <ProductRecommendationsDelegator
                    recommendationCollections={recommendationCollections.slice(1)}
                    loadingDiscoveryCollectionsRecommendations={loadingRecommendations}
                    productRecsByAlgoliaMetadata={{
                      title,
                      productRecommendationTitle: null,
                      productRecommendationsSearchUrl: null,
                      productRecsIndexName,
                      hitsPerPage: 0,
                    }}
                    shouldUseDiscoveryCollections
                  />
                </div>
              </div>
            )}
            {totalNumberOfArticles && totalNumberOfArticles > ITEMS_PER_CATEGORY_PAGE && (
              <Grid container justifyContent="center" css={styles.pagination}>
                <Grid item>
                  <PaginationControls
                    pageCount={pageCount}
                    currentPage={currentPageNumber}
                    maxPages={5}
                    renderLinks
                    largeStyle
                  />
                </Grid>
              </Grid>
            )}
            {bottomContentJson && <RichTextContentRenderer body={bottomContentJson} links={bottomContentLinks} />}
          </div>
        }
      />
      <CourseraMetatags
        metaNameAndProperties={{
          title: metaTitle,
          ogAndTwitterTitle: metaTitle,
          description: metaDescription,
          disableCourseraDescriptionSuffix: true,
        }}
        paginationProperties={paginationProperties}
        hrefLangOverrides={() => []}
      />
      <CategoryPageGoogleSchemaMarkup
        categoryPageUrl={pageUrl}
        categoryBreadcrumbList={[
          { name: 'Coursera Articles', absoluteUrl: path.join(urlDomain, hubPageLink) },
          { name: title, absoluteUrl: pageUrl },
        ]}
        categoryPageName={title}
      />
    </React.Fragment>
  );
};

export default Retracked.createTrackedContainer(() => ({
  namespace: {
    app: 'articles',
    page: 'article_hub_category',
  },
}))(CategoryPage);
